import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "indian-economy-key-data-statistics-cheat-sheet-2026",
  category: "subject-explainers",
  authorSlug: "dhairya-madaan",
  title: "Indian Economy Key Data & Statistics 2026: GDP, Inflation, Trade & Budget Cheat Sheet",
  description: "Quick reference for the latest official Indian economic data. Covers GDP, Inflation, RBI Rates, Fiscal Deficit, and Union Budget 2026-27 numbers.",
  pubDate: "2026-08-20",
  tags: ["indian-economy", "statistics", "budget", "cheat-sheet"],
  readMinutes: 12,
  heroEyebrow: "Subject Explainer · Indian Economy",
};

export function Body() {
  return (
    <article className="max-w-4xl mx-auto space-y-8">
      <header className="mb-10">
        <h1 className="text-4xl font-bold mb-4 text-primary">Indian Economy Key Data &amp; Statistics 2026</h1>
        <p className="text-xl text-muted-foreground">GDP, Inflation, Trade &amp; Budget Cheat Sheet (Sources: RBI, MoSPI, Economic Survey)</p>
      </header>

      <section>
        <h2 className="text-2xl font-bold mb-4 border-b border-rule pb-2">1. Growth &amp; National Income</h2>
        <p className="mb-2 text-sm text-muted-foreground">Source: MoSPI NSO Data / Economic Survey 2025-26</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Real GDP Growth Rate (FY 2025-26 Est.):</strong> ~6.5% - 7.0%</li>
          <li><strong>Nominal GDP Size:</strong> Projected to cross $4 Trillion USD mark.</li>
          <li><strong>Sectoral Composition of GVA:</strong>
            <ul className="list-circle pl-6 mt-1">
              <li>Agriculture &amp; Allied Sectors: ~15-18%</li>
              <li>Industry (Manufacturing, Construction, Mining): ~25-28%</li>
              <li>Services: ~54-57% (Dominant sector)</li>
            </ul>
          </li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-4 border-b border-rule pb-2">2. Inflation &amp; Monetary Policy</h2>
        <p className="mb-2 text-sm text-muted-foreground">Source: RBI Monetary Policy Committee (MPC) / RBI Bulletin</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Headline Retail Inflation (CPI):</strong> Targeting 4% (with a tolerance band of +/- 2%). Current trends hover around 4.5% - 5.5% depending on food price volatility.</li>
          <li><strong>Wholesale Price Index (WPI):</strong> Represents producer side inflation; heavily influenced by global commodity prices.</li>
          <li><strong>Key Policy Rates (Indicative / Latest MPC bounds):</strong>
            <ul className="list-circle pl-6 mt-1">
              <li>Repo Rate: Benchmark lending rate (~6.50% bound)</li>
              <li>Reverse Repo Rate / SDF Rate: Floor rate for liquidity absorption</li>
              <li>Marginal Standing Facility (MSF) Rate &amp; Bank Rate: Upper bound</li>
              <li>Cash Reserve Ratio (CRR): ~4.50%</li>
              <li>Statutory Liquidity Ratio (SLR): ~18.00%</li>
            </ul>
          </li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-4 border-b border-rule pb-2">3. Public Finance &amp; Union Budget 2026-27</h2>
        <p className="mb-2 text-sm text-muted-foreground">Source: Ministry of Finance, Budget Documents</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Fiscal Deficit Target:</strong> Aiming to consolidate below 4.5% of GDP by FY26.</li>
          <li><strong>Capital Expenditure (Capex):</strong> Continued strong push, exceeding Rs. 11-12 Lakh Crores, focusing on infrastructure (Railways, Roads, Defense).</li>
          <li><strong>Key Tax Metrics:</strong> Gross Tax Revenues seeing strong buoyancy, driven by GST collections (regularly crossing Rs. 1.6+ Lakh Crore monthly) and direct tax compliance.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-4 border-b border-rule pb-2">4. External Sector &amp; Trade</h2>
        <p className="mb-2 text-sm text-muted-foreground">Source: RBI / Ministry of Commerce</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Current Account Deficit (CAD):</strong> Manageable, typically ranging 1% - 2% of GDP, buffered by strong services exports and remittances.</li>
          <li><strong>Foreign Exchange Reserves:</strong> Healthy buffer, often crossing $600+ Billion USD.</li>
          <li><strong>FDI &amp; FPI:</strong> Strong structural foreign direct investment in manufacturing and tech; portfolio investments remain sensitive to global interest rates.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-4 border-b border-rule pb-2">5. Employment &amp; Social Schemes</h2>
        <p className="mb-2 text-sm text-muted-foreground">Source: PLFS (Periodic Labour Force Survey) / Budget</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Unemployment Rate (PLFS):</strong> Tracking rural and urban trends (Urban UR typically slightly higher but stabilizing). Labour Force Participation Rate (LFPR), especially female LFPR, shows gradual improvement.</li>
          <li><strong>Key Government Schemes:</strong>
            <ul className="list-circle pl-6 mt-1">
              <li><strong>PLI (Production Linked Incentive):</strong> Boosting manufacturing in 14 key sectors.</li>
              <li><strong>MGNREGA:</strong> Rural employment guarantee; allocations adjusted based on demand.</li>
              <li><strong>PM Vishwakarma:</strong> Support for traditional artisans and craftspeople.</li>
            </ul>
          </li>
        </ul>
      </section>

    </article>
  );
}


